import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@saleshandy/design-system';
import Spinner, {
  SpinnerAnimation,
} from '../../shared/design-system/components/atoms/spinner/spinner';
import {
  createSubscriptionRenewalCheckout,
  getSubscriptionRenewalStatus,
} from '../domains/helpers/subscription-renewal.api';
import './email-infra-pay.scss';

const REDIRECT_DELAY_MS = 3000;

const EmailInfraPay: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const orderId = params.get('orderId');
  const status = params.get('status');

  const [loading, setLoading] = useState(true);
  const [checkoutOpened, setCheckoutOpened] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(status === 'success');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const checkoutWindowRef = useRef<Window | null>(null);

  const checkPaymentStatus = useCallback(async () => {
    const orderIdNum = Number(orderId);
    if (!orderId || Number.isNaN(orderIdNum)) return;

    setChecking(true);
    try {
      const result = await getSubscriptionRenewalStatus();
      const renewal = result.renewals?.find((r) => r.orderId === orderIdNum);
      if (!renewal || renewal.renewalStatus === 'charged') {
        setPaymentSuccess(true);
        setCheckoutOpened(false);
      }
    } catch {
      // User can retry via button
    } finally {
      setChecking(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (status === 'success') {
      setPaymentSuccess(true);
      setLoading(false);
      return;
    }

    if (status === 'cancelled') {
      setError(t('messages.payment_cancelled'));
      setLoading(false);
      return;
    }

    const orderIdNum = Number(orderId);
    if (!orderId || Number.isNaN(orderIdNum)) {
      setError(t('messages.invalid_payment_link'));
      setLoading(false);
      return;
    }

    const initiateCheckout = async () => {
      try {
        const result = await createSubscriptionRenewalCheckout(orderIdNum);
        if (result.checkoutUrl) {
          const checkoutWindow = window.open(result.checkoutUrl, '_blank');
          if (!checkoutWindow || checkoutWindow.closed) {
            window.location.href = result.checkoutUrl;
            return;
          }
          checkoutWindowRef.current = checkoutWindow;
          setCheckoutOpened(true);
          setLoading(false);
        } else {
          setError(t('messages.checkout_session_error'));
          setLoading(false);
        }
      } catch (err) {
        setError(t('messages.generic_payment_error'));
        setLoading(false);
      }
    };

    initiateCheckout();
  }, [orderId, status]);

  useEffect(() => {
    if (!checkoutOpened) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkPaymentStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkoutOpened, checkPaymentStatus]);

  useEffect(() => {
    if (!paymentSuccess) return;

    const timer = setTimeout(() => {
      history.push('/');
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [paymentSuccess, history]);

  if (paymentSuccess) {
    return (
      <div className="email-infra-pay">
        <h2>{t('messages.payment_successful')}</h2>
        <p>{t('messages.subscription_renewed_redirecting')}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="email-infra-pay">
        <Spinner animation={SpinnerAnimation.Border} />
        <p>{t('messages.redirecting_to_payment')}</p>
      </div>
    );
  }

  if (checkoutOpened) {
    return (
      <div className="email-infra-pay">
        <h2>{t('messages.payment_in_progress')}</h2>
        <p>{t('messages.complete_payment_in_other_tab')}</p>
        <Button
          onClick={checkPaymentStatus}
          disabled={checking}
        >
          {checking
            ? t('labels.checking_status')
            : t('labels.check_payment_status')}
        </Button>
      </div>
    );
  }

  return (
    <div className="email-infra-pay">
      <h2>{t('messages.payment_issue')}</h2>
      <p>{error}</p>
      {orderId && (
        <Button onClick={() => window.location.reload()}>
          {t('labels.try_again')}
        </Button>
      )}
    </div>
  );
};

export default EmailInfraPay;
